import { useState, useEffect, useRef } from 'react';

export interface TravelPair {
  key: string; // `${origin}|${destination}` — stable cache key
  origin: string;
  destination: string;
}

let scriptLoadPromise: Promise<void> | null = null;

function loadGoogleMapsScript(apiKey: string): Promise<void> {
  if (window.google?.maps) return Promise.resolve();
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoadPromise = null; // allow retry on a later render rather than caching a failure forever
      reject(new Error('Failed to load Google Maps script'));
    };
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
}

/**
 * Batches Google Maps Distance Matrix lookups for a set of origin/destination address pairs,
 * caching results by pair key so re-renders (e.g. navigating back to an already-computed date)
 * don't re-fetch. Deduplicates addresses before calling the API — a cross-product of N unique
 * origins × M unique destinations in one request, rather than one request per pair, which keeps
 * this cheap even when the same "home address" origin repeats across many pairs (the common
 * case). Requires the Distance Matrix API to be enabled on the project behind
 * VITE_GOOGLE_MAPS_API_KEY (same key MemberMap.tsx already uses for the Maps JS API) — that's a
 * one-time manual step in Google Cloud Console, not something this code can do.
 *
 * Returns a Map from pair key -> duration in minutes. Callers should fall back to an estimate for
 * any pair not yet present (still loading, or the API call failed) — this hook fails silently by
 * design, since a missing travel estimate degrading gracefully is far better than the calendar
 * breaking because a map API call failed.
 */
export function useTravelDurations(pairs: TravelPair[], apiKey: string | undefined): Map<string, number> {
  const [durations, setDurations] = useState<Map<string, number>>(new Map());
  const fetchedKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!apiKey || pairs.length === 0) return;
    const unfetched = pairs.filter((p) => !fetchedKeysRef.current.has(p.key));
    if (unfetched.length === 0) return;

    let cancelled = false;

    loadGoogleMapsScript(apiKey)
      .then(() => {
        if (cancelled || !window.google?.maps?.DistanceMatrixService) return;

        const uniqueOrigins = Array.from(new Set(unfetched.map((p) => p.origin)));
        const uniqueDestinations = Array.from(new Set(unfetched.map((p) => p.destination)));

        const service = new window.google.maps.DistanceMatrixService();
        service.getDistanceMatrix(
          {
            origins: uniqueOrigins,
            destinations: uniqueDestinations,
            travelMode: window.google.maps.TravelMode.DRIVING,
            unitSystem: window.google.maps.UnitSystem.IMPERIAL,
          },
          (response: any, status: string) => {
            if (cancelled) return;
            unfetched.forEach((p) => fetchedKeysRef.current.add(p.key)); // don't retry failures every render
            if (status !== 'OK' || !response) return;

            setDurations((prev) => {
              const next = new Map(prev);
              unfetched.forEach((pair) => {
                const oIdx = uniqueOrigins.indexOf(pair.origin);
                const dIdx = uniqueDestinations.indexOf(pair.destination);
                const element = response.rows?.[oIdx]?.elements?.[dIdx];
                if (element?.status === 'OK' && typeof element.duration?.value === 'number') {
                  next.set(pair.key, Math.round(element.duration.value / 60));
                }
              });
              return next;
            });
          }
        );
      })
      .catch((err) => console.error('useTravelDurations: failed to load Google Maps script', err));

    return () => {
      cancelled = true;
    };
  }, [pairs, apiKey]);

  return durations;
}
